// Read-only and exploration pages use the small shared bundle. CodeMirror and
// Vim are fetched only once an editor is present (FR-EDIT-02, FR-UI-08).
// The module request is same-origin under the page's script CSP; CodeMirror
// reads the page nonce itself before installing its styles (FR-UI-07).

const mounted = new WeakSet<HTMLElement>();
const buffers = new WeakMap<
  HTMLTextAreaElement,
  { edited: boolean; onInput: () => void }
>();

/** Submits that need the current buffer and its base version (FR-MU-03). */
const TAKES_BUFFER =
  /\/(messages|regenerate|style-review|buffer\/start-from-latest)$/;

/** Keep the first editor action until its buffer hooks are installed. */
function holdSubmits(
  container: HTMLElement,
  textarea: HTMLTextAreaElement,
): () => void {
  let pending: { form: HTMLFormElement; submitter: HTMLElement | null } | null =
    null;
  const hold = (event: SubmitEvent) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form !== textarea.form && !TAKES_BUFFER.test(form.action)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pending ??= { form, submitter: event.submitter };
  };
  document.addEventListener("submit", hold, true);
  return () => {
    document.removeEventListener("submit", hold, true);
    if (!pending || !container.isConnected || !pending.form.isConnected) return;
    if (pending.submitter && !pending.submitter.isConnected) return;
    pending.form.requestSubmit(pending.submitter);
  };
}

async function enhanceEditor(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#editor");
  if (!container || mounted.has(container)) return;
  const textarea = container.querySelector("textarea");
  if (!textarea) return;
  mounted.add(container);
  // A browser-restored value can belong to an older revision (FR-MU-03),
  // so only actual input during this fetch is carried into CodeMirror.
  let buffer = buffers.get(textarea);
  if (!buffer) {
    // Start interim typing on the served revision, as mountEditor does. Keep
    // the watcher after a failed fetch, so retrying never discards new input.
    textarea.value = textarea.defaultValue;
    const pending = {
      edited: false,
      onInput: () => {
        pending.edited = true;
      },
    };
    buffers.set(textarea, pending);
    textarea.addEventListener("input", pending.onInput);
    buffer = pending;
  }
  // htmx can already send while this chunk downloads. Hold Save and the
  // buffer-taking forms so their first request includes the text just typed.
  const release = holdSubmits(container, textarea);
  const editor = await import("./editor").catch(() => null);
  if (!editor || !container.isConnected) {
    // A failed fetch leaves the plain textarea usable. A later swap can retry.
    mounted.delete(container);
    release();
    return;
  }
  // The textarea remains usable while the chunk loads. Mount on the server's
  // original text, then apply any typing as an edit so autosave and Undo agree.
  const typed = textarea.value;
  const changed = buffer.edited && typed !== textarea.defaultValue;
  textarea.removeEventListener("input", buffer.onInput);
  buffers.delete(textarea);
  const focused = document.activeElement === textarea;
  const selection = {
    anchor: textarea.selectionStart,
    head: textarea.selectionEnd,
  };
  const view = editor.mountEditor(container);
  if (!view) {
    release();
    return;
  }
  if (changed) {
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: typed },
    });
  }
  if (focused) {
    view.dispatch({
      selection: {
        anchor: Math.min(selection.anchor, view.state.doc.length),
        head: Math.min(selection.head, view.state.doc.length),
      },
    });
    view.focus();
  }
  release();
}

void enhanceEditor();
// Morphs preserve the mounted container; a fragment that introduces one is
// enhanced as well, without remounting after every conversation update.
document.addEventListener("htmx:after:settle", () => void enhanceEditor());
